import { chain } from '@leprecoins/effector';
import { createSpine } from '@leprecoins/effector-pixi';
import { Easing, Tween } from '@tweenjs/tween.js';
import { attach, combine, createEffect, createEvent, createStore, sample } from 'effector';
import { and, combineEvents, delay, equals, not, previous } from 'patronum';
import { gameModel, ONBOARDING_STEPS } from 'entities/game';
import { receiveRefresh, receiveTurn, sendBet, TURN_STATUSES, TurnStatus } from 'shared/api';
import { animate, createModalSwitcher } from 'shared/lib';
import {
	INTERACTIVE_ELEMENT_CENTER_COLUMN,
	INTERACTIVE_ELEMENT_GATES_ANIMATIONS,
	INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS,
	INTERACTIVE_ELEMENT_MOVE_SIDES,
	INTERACTIVE_ELEMENT_MESSAGES,
	InteractiveElementBall,
	InteractiveElementGatesAnimation,
	InteractiveElementGoalkeeperAnimation,
	InteractiveElementMoveSide,
	InteractiveElementMessage,
} from '../config';

const setup = createEvent();

const goalkeeperSpine = createSpine<InteractiveElementGoalkeeperAnimation, ''>({
	setup,
	atlas: 'spine/interactive-element/interactive-element.atlas',
	skeleton: 'spine/interactive-element/interactive-element.json',
});

const gatesSpine = createSpine<InteractiveElementGatesAnimation, ''>({
	setup,
	atlas: 'spine/gates/gates.atlas',
	skeleton: 'spine/gates/gates.json',
});

const $ball = createStore<InteractiveElementBall>({
	x: 0,
	y: 0,
	size: 0,
	visible: false,
});

const messageSwitcher = createModalSwitcher();

const $messageVariant = createStore<InteractiveElementMessage | null>(null);

const $isRoundFinished = createStore(false);

const $goalkeeperShift = createStore(0);

const $currentColumn = createStore(INTERACTIVE_ELEMENT_CENTER_COLUMN);

const $prevColumn = previous($currentColumn);

const $sideMoving = createStore<InteractiveElementMoveSide | null>(null);

const $moveDifference = createStore(0);

const $profit = createStore(0);

const $messageCanVisible = combine(
	{
		status: gameModel.$turnStatus,
		pending: gameModel.$sendBetPending,
	},
	({ status, pending }) => !pending && status !== TURN_STATUSES.bet,
);

const $enableOnboardingCalculator = and(
	gameModel.$isOnboarding,
	equals(gameModel.$onboardingStep, ONBOARDING_STEPS.BET_CALCULATOR),
);

const setGoalkeeperShift = createEvent<number>();

const animateGoalkeeper = createEvent<TurnStatus>();

const updateBall = createEvent<InteractiveElementBall>();

const animateGoal = createEvent();

const playAgain = createEvent();

const moveGoalkeeperFx = attach({
	source: { side: $sideMoving, diff: $moveDifference, shift: $goalkeeperShift },
	effect: ({ side, diff, shift }) => {
		if (!side) return;

		const properties = { shift };

		const newX =
			side === INTERACTIVE_ELEMENT_MOVE_SIDES.right ? diff * 100 + shift : shift - diff * 100;

		let timeout: NodeJS.Timeout;

		const tween = new Tween(properties)
			.easing(Easing.Linear.None)
			.to({ shift: newX }, diff * 100)
			.onUpdate(() => setGoalkeeperShift(properties.shift))
			.onComplete(() => {
				clearInterval(timeout);
			})
			.start();

		timeout = animate(tween);
	},
});

const setStartedPositionBallFx = createEffect(
	(status: TurnStatus): InteractiveElementBall | null => {
		const selector =
			status === TURN_STATUSES.lose
				? "#game-field [data-variant='mistake']"
				: "#game-field [data-variant='ball']";

		const cellNode = window.document.querySelector(selector);
		const interactiveElementNode = window.document.querySelector('#interactive-element');

		if (!cellNode || !interactiveElementNode) return null;

		const { x, y, height, width } = cellNode.getBoundingClientRect();
		const { width: iWidth } = interactiveElementNode.getBoundingClientRect();

		const size = 0.115 * iWidth;

		return {
			size,
			y: y + height / 2 - size / 2,
			x: x + width / 2 - size / 2,
			visible: true,
		};
	},
);

const animateBallFx = attach({
	source: { ball: $ball, shift: $goalkeeperShift, status: gameModel.$turnStatus },
	effect: ({ ball, shift, status }) => {
		const node = window.document.querySelector('#interactive-element');

		const { scale } = (window.document.querySelector('#game-page-wrapper') as HTMLDivElement).style;

		if (!node) return;

		const {
			x: canvasX,
			y: canvasY,
			height: canvasHeight,
			width: canvasWidth,
		} = node.getBoundingClientRect();

		const [canvasEndX, canvasEndY] = [
			status === TURN_STATUSES.win
				? canvasX + canvasWidth * 0.34
				: canvasX + canvasWidth / 2.02 + (shift / 100) * (canvasWidth / 30),
			status === TURN_STATUSES.win ? canvasY + canvasHeight * 0.56 : canvasY + canvasHeight / 1.8,
		] as const;

		const properties = { ...ball };

		const speed = 2500 * Number(scale);

		const dx = canvasEndX - ball.x;

		const dy = canvasEndY - ball.y;

		const distance = Math.sqrt(dx * dx + dy * dy);

		const duration = (distance / speed) * 1000;

		const direction = Math.random() > 0.5 ? 1 : -1;

		const maxDirectionDelta = canvasWidth * 0.2 * Math.max(Math.random(), 0.5) * direction;

		let timeout: NodeJS.Timeout;

		let goalkeeperAnimated = false;

		const tween = new Tween(properties)
			.easing(Easing.Linear.None)
			.to(
				{
					...ball,
					x: canvasEndX - properties.size / 2,
					y: canvasEndY - properties.size / 2,
				},
				duration,
			)
			.onUpdate((_, elapsed) => {
				updateBall({
					...properties,
					x: properties.x + maxDirectionDelta * Math.sin(elapsed * Math.PI),
					visible: (1 - elapsed) * duration >= 50,
				});

				if (
					(1 - elapsed) * duration <= 200 &&
					status === TURN_STATUSES.win &&
					!goalkeeperAnimated
				) {
					animateGoalkeeper(status!);
					goalkeeperAnimated = true;
				}
			})
			.onComplete(() => {
				updateBall({ ...properties, visible: false });
				status !== TURN_STATUSES.win && animateGoalkeeper(status!);

				status === TURN_STATUSES.win && animateGoal();

				clearInterval(timeout);
			})
			.start();

		timeout = animate(tween);
	},
});

sample({
	clock: gameModel.gameFieldAnimated,
	filter: $messageCanVisible,
	target: messageSwitcher.enable,
});

chain(playAgain, messageSwitcher.disable);

chain(sendBet, messageSwitcher.disable);

sample({
	clock: gameModel.$turnStatus,
	filter: (status) => status === TURN_STATUSES.lose,
	fn: () => INTERACTIVE_ELEMENT_MESSAGES.luckNextTime,
	target: $messageVariant,
});

sample({
	clock: receiveTurn.success,
	filter: ({ status }) => status === TURN_STATUSES.win,
	fn: () => INTERACTIVE_ELEMENT_MESSAGES.goodGame,
	target: $messageVariant,
});

sample({
	clock: gameModel.win,
	fn: () => INTERACTIVE_ELEMENT_MESSAGES.skillIncredible,
	target: $messageVariant,
});

sample({
	clock: playAgain,
	source: {
		wager: gameModel.$wager,
		balance: gameModel.$balance,
		clientSeed: gameModel.$clientSeed,
	},
	filter: ({ wager, balance }) => Number(wager) <= balance,
	fn: ({ wager, clientSeed }) => ({ wager: Number(wager), clientSeed }),
	target: sendBet,
});

sample({
	clock: receiveTurn.success,
	fn: ({ profit }) => profit,
	target: $profit,
});

sample({
	clock: goalkeeperSpine.$spine,
	filter: Boolean,
	fn: () => ({ name: INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS.idle, loop: true }),
	target: goalkeeperSpine.play,
});

sample({
	clock: gatesSpine.$spine,
	filter: Boolean,
	fn: () => ({ name: INTERACTIVE_ELEMENT_GATES_ANIMATIONS.default }),
	target: gatesSpine.play,
});

sample({
	clock: receiveTurn.success,
	filter: ({ status }) => status !== TURN_STATUSES.bet,
	fn: () => true,
	target: $isRoundFinished,
});

sample({
	clock: animateGoalkeeper,
	filter: not(gameModel.$sendBetPending),
	fn: (status) => {
		if (status === TURN_STATUSES.win)
			return { name: INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS.win };

		return { name: INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS.lose };
	},
	target: goalkeeperSpine.play,
});

sample({
	clock: animateGoal,
	filter: not(gameModel.$sendBetPending),
	fn: () => ({ name: INTERACTIVE_ELEMENT_GATES_ANIMATIONS.goal, timeScale: 1.2 }),
	target: gatesSpine.play,
});

sample({
	clock: $currentColumn,
	filter: not($isRoundFinished),
	target: $sideMoving.reinit,
});

sample({
	clock: $currentColumn,
	source: $prevColumn,
	filter: not($isRoundFinished),
	fn: (prev, current) => {
		if (prev === null)
			return current > INTERACTIVE_ELEMENT_CENTER_COLUMN
				? INTERACTIVE_ELEMENT_MOVE_SIDES.right
				: INTERACTIVE_ELEMENT_MOVE_SIDES.left;

		return current > prev
			? INTERACTIVE_ELEMENT_MOVE_SIDES.right
			: INTERACTIVE_ELEMENT_MOVE_SIDES.left;
	},
	target: $sideMoving,
});

sample({
	clock: $currentColumn,
	source: $prevColumn,
	filter: not($isRoundFinished),
	fn: (prev, current) => {
		if (prev === null) return Math.abs(current - INTERACTIVE_ELEMENT_CENTER_COLUMN);

		return Math.abs(current - prev);
	},
	target: $moveDifference,
});

sample({
	clock: $sideMoving,
	source: $moveDifference,
	filter: (_, side) => !!side,
	fn: (diff, side) => {
		const animation =
			side === INTERACTIVE_ELEMENT_MOVE_SIDES.right
				? INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS.rightStep
				: INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS.leftStep;

		return Array(Math.min(diff, 2)).fill(animation);
	},
	target: createEffect((animations: InteractiveElementGoalkeeperAnimation[]) => {
		goalkeeperSpine.play({
			name: INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS.idle,
			loop: false,
			timeScale: 1000000,
		});

		animations.forEach((animation) => goalkeeperSpine.add({ name: animation, timeScale: 6 }));
		goalkeeperSpine.add({ name: INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS.idle, loop: true });
		moveGoalkeeperFx();
	}),
});

sample({
	clock: [receiveRefresh.success, receiveTurn.success],
	filter: not($isRoundFinished),
	fn: ({ turnHistory }) => turnHistory.at(-1) ?? INTERACTIVE_ELEMENT_CENTER_COLUMN,
	target: $currentColumn,
});

sample({
	clock: delay(receiveTurn.success, 0),
	filter: $isRoundFinished,
	fn: ({ status }) => status,
	target: setStartedPositionBallFx,
});

sample({
	clock: setStartedPositionBallFx.doneData,
	filter: Boolean,
	target: $ball,
});

sample({
	clock: setStartedPositionBallFx.doneData,
	filter: Boolean,
	target: animateBallFx,
});

chain(setGoalkeeperShift, $goalkeeperShift);

chain(sendBet, $isRoundFinished.reinit);

chain(sendBet, $currentColumn.reinit);

sample({
	clock: sendBet,
	fn: () => ({ name: INTERACTIVE_ELEMENT_GATES_ANIMATIONS.default }),
	target: gatesSpine.play,
});

sample({
	clock: sendBet,
	fn: () => ({ name: INTERACTIVE_ELEMENT_GOALKEEPER_ANIMATIONS.idle, loop: true }),
	target: goalkeeperSpine.play,
});

chain(updateBall, $ball);

sample({
	clock: combineEvents([goalkeeperSpine.$spine.updates, gatesSpine.$spine.updates]),
	filter: ([goalkeeper, gates]) => goalkeeper !== null && gates !== null,
	target: gameModel.assetsLoaded,
});

export const interactiveElementModel = {
	$moveDifference,
	$goalkeeperShift,
	$ball,
	$messageVariant,
	$profit,
	playAgain,
	messageSwitcher,
	goalkeeperSpine,
	gatesSpine,
	setup,
	$enableOnboardingCalculator,
};
